import React, { useState, useEffect } from 'react';
import { X, Paperclip } from 'lucide-react';
import { Input } from './Form/Input';
import { RichTextEditor } from './Form/RichTextEditor';
import { Button } from './ui/Button';
import { VERIFIED_SENDER_EMAIL } from '../constants/email';

export interface SendEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (data: EmailFormData) => Promise<void>;
  defaultSubject: string;
  defaultBody: string;
  attachmentNames: string[];
  /** Default Reply-To address (the sender's own email); editable by the user. */
  defaultReplyTo?: string;
}

export interface EmailFormData {
  replyTo: string;
  recipients: string[];
  cc: string[];
  subject: string;
  body: string;
  bodyPlainText: string;
}

/**
 * Modal for sending emails with attachments.
 * Matches existing app styling (Oxygen font, #7CB376 green, neutral colors).
 */
export const SendEmailModal: React.FC<SendEmailModalProps> = ({
  isOpen,
  onClose,
  onSend,
  defaultSubject,
  defaultBody,
  attachmentNames,
  defaultReplyTo,
}) => {
  const [replyTo, setReplyTo] = useState(defaultReplyTo || '');
  const [recipients, setRecipients] = useState('');
  const [cc, setCc] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState(defaultBody);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');
  const [replyToError, setReplyToError] = useState('');
  const [recipientsError, setRecipientsError] = useState('');
  const [subjectError, setSubjectError] = useState('');
  const [bodyError, setBodyError] = useState('');

  // Reset form when modal opens with new defaults
  useEffect(() => {
    if (isOpen) {
      setReplyTo(defaultReplyTo || '');
      setRecipients('');
      setCc('');
      setRecipientName('');
      setSubject(defaultSubject);
      setBody(defaultBody);
      setError('');
      setReplyToError('');
      setRecipientsError('');
      setSubjectError('');
      setBodyError('');
      setIsSending(false);
    }
  }, [isOpen, defaultSubject, defaultBody, defaultReplyTo]);

  // Auto-update "Hi <name>," greeting when recipient name changes
  useEffect(() => {
    if (!isOpen) return;
    
    // Check if body starts with "Hi" (might be wrapped in <p> tags from editor)
    const textContent = body.replace(/<[^>]*>/g, '').trim();
    const hiMatch = textContent.match(/^Hi[,\s]/i);
    if (!hiMatch) return;
    
    if (recipientName.trim()) {
      // Replace "Hi," or "Hi <name>," with "Hi <name>," (handle both plain and HTML)
      const newBody = body
        .replace(/^(<p>)?Hi(?:\s+[^,<]+)?,/i, `$1Hi ${recipientName.trim()},`)
        .replace(/^Hi(?:\s+[^,<]+)?,/i, `Hi ${recipientName.trim()},`);
      setBody(newBody);
    } else {
      // No name - revert to "Hi,"
      const newBody = body
        .replace(/^(<p>)?Hi(?:\s+[^,<]+)?,/i, '$1Hi,')
        .replace(/^Hi(?:\s+[^,<]+)?,/i, 'Hi,');
      setBody(newBody);
    }
  }, [recipientName]); // Only depend on recipientName, not body

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const parseEmailList = (input: string): string[] => {
    if (!input.trim()) return [];
    return input
      .split(',')
      .map((e) => e.trim())
      .filter((e) => e.length > 0);
  };

  const validateForm = (): boolean => {
    let isValid = true;

    // Validate reply-to address
    if (!replyTo.trim()) {
      setReplyToError('Reply-to address is required');
      isValid = false;
    } else if (!validateEmail(replyTo.trim())) {
      setReplyToError('Invalid email address');
      isValid = false;
    } else {
      setReplyToError('');
    }

    // Validate recipients
    const recipientList = parseEmailList(recipients);
    if (recipientList.length === 0) {
      setRecipientsError('At least one recipient is required');
      isValid = false;
    } else {
      const invalidEmails = recipientList.filter((email) => !validateEmail(email));
      if (invalidEmails.length > 0) {
        setRecipientsError(`Invalid email(s): ${invalidEmails.join(', ')}`);
        isValid = false;
      } else {
        setRecipientsError('');
      }
    }

    // Validate CC (optional, but if provided must be valid)
    const ccList = parseEmailList(cc);
    if (ccList.length > 0) {
      const invalidCc = ccList.filter((email) => !validateEmail(email));
      if (invalidCc.length > 0) {
        setError(`Invalid CC email(s): ${invalidCc.join(', ')}`);
        isValid = false;
      }
    }

    // Validate subject
    if (!subject.trim()) {
      setSubjectError('Subject is required');
      isValid = false;
    } else {
      setSubjectError('');
    }

    // Validate body (strip HTML tags since Quill's empty state is "<p><br></p>")
    const bodyText = body.replace(/<[^>]*>/g, '').trim();
    if (!bodyText) {
      setBodyError('Message is required');
      isValid = false;
    } else {
      setBodyError('');
    }

    return isValid;
  };

  const handleSend = async () => {
    setError('');

    if (!validateForm()) {
      return;
    }

    setIsSending(true);

    try {
      // RichTextEditor already outputs HTML, so use it directly
      const htmlBody = body.trim();
      
      // Convert HTML to plain text for email clients that don't support HTML
      const plainText = htmlBody
        .replace(/<\/p>/gi, '\n\n')
        .replace(/<\/div>/gi, '\n')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/li>/gi, '\n')
        .replace(/<\/h[1-6]>/gi, '\n\n')
        .replace(/<[^>]*>/g, '')
        .replace(/\n\n\n+/g, '\n\n')
        .trim();

      await onSend({
        replyTo: replyTo.trim(),
        recipients: parseEmailList(recipients),
        cc: parseEmailList(cc),
        subject: subject.trim(),
        body: htmlBody,
        bodyPlainText: plainText,
      });

      // Success - close modal
      onClose();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to send email. Please try again.';
      setError(errorMessage);
    } finally {
      setIsSending(false);
    }
  };

  const handleCancel = () => {
    if (!isSending) {
      onClose();
    }
  };

  // Check if required fields are filled (for disabling Send button)
  const bodyText = body.replace(/<[^>]*>/g, '').trim();
  const isFormValid =
    replyTo.trim().length > 0 &&
    recipients.trim().length > 0 &&
    subject.trim().length > 0 &&
    bodyText.length > 0;

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/25 overflow-y-auto py-8"
    >
      <div
        className="bg-white rounded-lg shadow-lg w-full max-w-2xl mx-4"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <h2
            className="text-lg font-semibold text-neutral-900"
            style={{ fontFamily: 'Oxygen' }}
          >
            Send Email
          </h2>
          <button
            onClick={handleCancel}
            disabled={isSending}
            className="text-neutral-500 hover:text-neutral-700 disabled:opacity-50"
            aria-label="Close"
          >
            <X size={24} strokeWidth={2} aria-hidden="true" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4">
          {/* Error banner */}
          {error && (
            <div className="bg-error-50 border border-error-200 text-error-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* From field — read-only; the sender is fixed to the verified
              address and enforced server-side, so it cannot be edited. */}
          <Input
            label="From *"
            type="email"
            value={VERIFIED_SENDER_EMAIL}
            readOnly
            tabIndex={-1}
            className="bg-neutral-100 text-neutral-600 cursor-not-allowed"
            hint="All emails are sent from Ministry of Wine's verified address."
          />

          {/* Reply-To field — editable; where recipient replies are directed */}
          <Input
            label="Reply-To *"
            type="email"
            placeholder="your.email@ministryofwine.co.nz"
            value={replyTo}
            onChange={(e) => setReplyTo(e.target.value)}
            error={replyToError}
            disabled={isSending}
            hint="Replies from recipients go to this address."
          />

          {/* To field */}
          <Input
            label="To *"
            type="text"
            placeholder="recipient@example.com, another@example.com"
            value={recipients}
            onChange={(e) => setRecipients(e.target.value)}
            error={recipientsError}
            disabled={isSending}
            hint="Separate multiple emails with commas"
          />

          {/* CC field */}
          <Input
            label="CC"
            type="text"
            placeholder="cc@example.com (optional)"
            value={cc}
            onChange={(e) => setCc(e.target.value)}
            disabled={isSending}
          />

          {/* Recipient Name field */}
          <Input
            label="Recipient Name"
            type="text"
            placeholder="e.g., John (optional)"
            value={recipientName}
            onChange={(e) => setRecipientName(e.target.value)}
            disabled={isSending}
            hint="Will populate 'Hi <name>,' in the message"
          />

          {/* Subject field */}
          <Input
            label="Subject *"
            type="text"
            placeholder="Email subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            error={subjectError}
            disabled={isSending}
          />

          {/* Body field - Rich Text Editor */}
          <div className="mb-4">
            <RichTextEditor
              label="Message *"
              placeholder="Email message"
              value={body}
              onChange={setBody}
              error={bodyError}
              disabled={isSending}
              rows={8}
            />
          </div>

          {/* Attachments */}
          <div>
            <label
              className="block text-neutral-700 mb-1.5"
              style={{
                fontFamily: 'Oxygen',
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '20px',
              }}
            >
              Attachments:
            </label>
            <div className="space-y-1">
              {attachmentNames.map((name, index) => (
                <div
                  key={index}
                  className="flex items-center text-sm text-neutral-600"
                  style={{ fontFamily: 'Oxygen' }}
                >
                  <Paperclip size={14} className="mr-2 flex-shrink-0 text-neutral-500" aria-hidden="true" />
                  <span>{name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-neutral-200">
          <Button variant="ghost" onClick={handleCancel} disabled={isSending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSend}
            disabled={isSending || !isFormValid}
            isLoading={isSending}
            style={{ backgroundColor: '#7CB376', borderColor: '#7CB376' }}
            className="hover:bg-[#6da265]"
          >
            {isSending ? 'Sending...' : 'Send'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SendEmailModal;
